import type { ReactElement, ReactNode } from "react";
import {
  Children,
  isValidElement,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";

import { cn } from "~/lib/ui/utils/utils";

const NAV_TAB_BRAND = Symbol.for("@inference-net/ui/nav-tab");

type NavTabItem = {
  value: string;
  label: ReactNode;
  count?: number;
  icon?: React.ComponentType<{
    className?: string;
  }>;
};
type NavTabsProps = {
  value: string;
  onValueChange: (value: string) => void;
  children: ReactNode;
  className?: string;
};
type NavTabProps = {
  value: string;
  children: ReactNode;
  count?: number;
  icon?: React.ComponentType<{
    className?: string;
  }>;
};

// NavTab is a declarative config element — rendering is handled by NavTabs.
function NavTab(_props: NavTabProps) {
  return null;
}
NavTab.displayName = "NavTab";
Object.defineProperty(NavTab, NAV_TAB_BRAND, {
  value: true,
  enumerable: false,
});

function isNavTabElement(child: ReactNode): child is ReactElement<NavTabProps> {
  if (!isValidElement<NavTabProps>(child)) return false;
  const type = child.type;
  // Prefer a brand marker so HMR / compiler rewrites can't empty the tab list
  // when function identity changes across module reloads.
  if (
    typeof type === "function" &&
    (type as unknown as Record<symbol, unknown>)[NAV_TAB_BRAND] === true
  ) {
    return true;
  }
  return type === NavTab;
}

function updateNavTabsIndicator({
  container,
  hasAnimated,
  indicator,
  tab,
}: {
  container: HTMLDivElement | null;
  hasAnimated: { current: boolean };
  indicator: HTMLDivElement | null;
  tab: HTMLButtonElement | undefined;
}) {
  if (!tab || !container || !indicator) return;
  const containerRect = container.getBoundingClientRect();
  const tabRect = tab.getBoundingClientRect();

  // The indicator lives in the container's scroll content, so position it in
  // content coordinates (viewport delta + scrollLeft at measure time).
  const left = tabRect.left - containerRect.left + container.scrollLeft;
  const width = tabRect.width;

  // Keep the active tab visible when the tab row overflows horizontally.
  if (tabRect.left < containerRect.left) {
    container.scrollLeft += tabRect.left - containerRect.left;
  } else if (tabRect.right > containerRect.right) {
    container.scrollLeft += tabRect.right - containerRect.right;
  }

  // First render: position instantly without transition.
  if (!hasAnimated.current) {
    indicator.style.transition = "none";
    indicator.style.transform = `translateX(${left}px)`;
    indicator.style.width = `${width}px`;
    requestAnimationFrame(() => {
      indicator.style.transition = "";
      hasAnimated.current = true;
    });
  } else {
    indicator.style.transform = `translateX(${left}px)`;
    indicator.style.width = `${width}px`;
  }
}

function NavTabs({ value, onValueChange, children, className }: NavTabsProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const indicatorRef = useRef<HTMLDivElement>(null);
  const tabRefs = useRef<Map<string, HTMLButtonElement> | null>(null);
  const hasAnimated = useRef(false);
  const [isScrollable, setIsScrollable] = useState(false);

  // Extract tab config from children
  const tabs = (() => {
    const items: NavTabItem[] = [];
    for (const child of Children.toArray(children)) {
      if (isNavTabElement(child)) {
        const props = child.props;
        items.push({
          value: props.value,
          label: props.children,
          count: props.count,
          icon: props.icon,
        });
      }
    }
    return items;
  })();

  const tabValuesKey = tabs.map((tab) => tab.value).join("\0");

  // Update on value change, mount, and when tabs change
  useLayoutEffect(() => {
    updateNavTabsIndicator({
      container: containerRef.current,
      hasAnimated,
      indicator: indicatorRef.current,
      tab: tabRefs.current?.get(value),
    });
  }, [tabValuesKey, value]);

  // Handle resize, and track whether the strip overflows to show the fade.
  // ResizeObserver fires once asynchronously as soon as it starts observing,
  // so this also covers the initial measurement without a synchronous call.
  useEffect(() => {
    const container = containerRef.current;
    const observer = new ResizeObserver(() => {
      updateNavTabsIndicator({
        container: containerRef.current,
        hasAnimated,
        indicator: indicatorRef.current,
        tab: tabRefs.current?.get(value),
      });
      if (container) {
        setIsScrollable(container.scrollWidth > container.clientWidth);
      }
    });
    if (container) {
      observer.observe(container);
    }
    return () => observer.disconnect();
  }, [value, tabValuesKey]);
  const setTabRef = (tabValue: string) => (el: HTMLButtonElement | null) => {
    const map = (tabRefs.current ??= new Map());
    if (el) {
      map.set(tabValue, el);
    } else {
      map.delete(tabValue);
    }
  };
  return (
    <div className="relative">
      <div
        ref={containerRef}
        className={cn(
          "relative flex items-end gap-6 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden",
          className,
        )}
        role="tablist"
      >
        {tabs.map((tab) => {
          const isActive = tab.value === value;
          const Icon = tab.icon;
          return (
            <button
              key={tab.value}
              ref={setTabRef(tab.value)}
              type="button"
              role="tab"
              aria-selected={isActive}
              onClick={() => onValueChange(tab.value)}
              className={cn(
                "relative inline-flex shrink-0 items-center gap-2 whitespace-nowrap pb-2.5 pt-1 text-sm font-medium transition-colors duration-150",
                "outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background rounded-sm",
                isActive
                  ? "text-foreground"
                  : "text-muted-foreground hover:text-foreground/80",
              )}
            >
              {Icon ? <Icon className="h-4 w-4" /> : null}
              {tab.label}
              {tab.count != null && tab.count > 0 ? (
                <span className="rounded-full bg-primary/10 px-1.5 py-0.5 text-[10px] font-semibold tabular-nums leading-none text-primary">
                  {tab.count}
                </span>
              ) : null}
            </button>
          );
        })}

        {/* Sliding indicator */}
        <div
          ref={indicatorRef}
          className="pointer-events-none absolute bottom-0 left-0 h-[2px] bg-foreground transition-all duration-200 ease-out"
        />
      </div>

      {/* Right-edge fade hints that the tab strip scrolls further, only
          shown once it actually overflows its container. */}
      {isScrollable && (
        <div
          className={`
            pointer-events-none absolute inset-y-0 right-0 w-8
            bg-gradient-to-l from-background to-transparent
          `}
        />
      )}
    </div>
  );
}
export { NavTabs, NavTab };
